import { Attachment } from '../types';
import { Download, Trash2 } from 'lucide-react';
import { formatFileSize, getFileIcon, getFileExtension, getFileColor } from '../utils/fileHelpers';
import { useTaskStore } from '../store/taskStore';

interface FileListProps {
  taskId: number;
  files: Attachment[];
}

export default function FileList({ taskId, files }: FileListProps) {
  const { deleteAttachment } = useTaskStore();

  if (files.length === 0) {
    return (
      <div className="text-center py-8 text-soft-muted">
        <p className="text-sm">Henüz dosya yüklenmemiş</p>
      </div>
    );
  }

  const handleDownload = (file: Attachment) => {
    const url = `${import.meta.env.VITE_API_URL}/api/uploads/${file.filename}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = file.original_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (fileId: number) => {
    if (confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) {
      try {
        await deleteAttachment(taskId, fileId);
      } catch (error) {
        console.error('Failed to delete file:', error);
      }
    }
  };

  return (
    <div className="space-y-2">
      {files.map((file) => {
        const FileIconComponent = getFileIcon(file.mime_type);
        const fileColor = getFileColor(file.mime_type);
        const extension = getFileExtension(file.original_name);

        return (
          <div
            key={file.id}
            className="flex items-center gap-3 p-3 bg-soft-gray rounded-lg hover:bg-gray-100 transition-colors group"
          >
            {/* File Icon */}
            <div className={`flex-shrink-0 ${fileColor}`}>
              <FileIconComponent className="w-8 h-8" />
            </div>

            {/* File Info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {file.original_name}
              </p>
              <div className="flex items-center gap-2 text-xs text-soft-muted">
                <span>{formatFileSize(file.size)}</span>
                {extension && (
                  <>
                    <span>•</span>
                    <span>{extension}</span>
                  </>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleDownload(file)}
                className="p-2 text-soft-muted hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                title="İndir"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(file.id)}
                className="p-2 text-soft-muted hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Sil"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
